import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/i18n";
import { MeetIcon, ZoomIcon } from "@/components/MeetIcons";
import { CalendarCheck, NotebookPen, Video, BookMarked, ClipboardList } from "lucide-react";

const BASE = "inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors";
const SOFT = "medium";
const PALETTES = {
  solid: {
    zoom: "bg-blue-600 hover:bg-blue-700 text-white border-transparent",
    meet: "bg-orange-500 hover:bg-orange-600 text-white border-transparent",
    attendance: "bg-sky-400 hover:bg-sky-500 text-white border-transparent",
    notebook: "bg-emerald-600 hover:bg-emerald-700 text-white border-transparent",
    journal: "bg-violet-600 hover:bg-violet-700 text-white border-transparent",
    homework: "bg-amber-400 hover:bg-amber-500 text-slate-900 border-transparent",
  },
  medium: {
    zoom: "bg-blue-200 hover:bg-blue-300 text-blue-900 border-blue-300",
    meet: "bg-orange-200 hover:bg-orange-300 text-orange-900 border-orange-300",
    attendance: "bg-cyan-200 hover:bg-cyan-300 text-cyan-900 border-cyan-300",
    notebook: "bg-emerald-200 hover:bg-emerald-300 text-emerald-900 border-emerald-300",
    journal: "bg-violet-200 hover:bg-violet-300 text-violet-900 border-violet-300",
    homework: "bg-amber-200 hover:bg-amber-300 text-amber-900 border-amber-300",
  },
  pastel: {
    zoom: "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200",
    meet: "bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200",
    attendance: "bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border-cyan-200",
    notebook: "bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200",
    journal: "bg-violet-50 hover:bg-violet-100 text-violet-700 border-violet-200",
    homework: "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-200",
  },
};
const STYLE = PALETTES[SOFT];

/** The action row of a class card: Zoom/Meet · Attendance · Notebook · Journal · Homework. */
export const ClassCardActions = ({ group: g, student = false }) => {
  const { t } = useI18n();
  const p = student ? "student" : "teacher";
  const links = student
    ? {
        attendance: "/dashboard/my-attendance",
        notebook: `/dashboard/my-notebook?group=${g.id}`,
        journal: "/dashboard/my-journal",
        homework: "/dashboard/my-homework",
      }
    : {
        attendance: `/dashboard/attendance?group=${g.id}`,
        notebook: `/dashboard/notebooks?group=${g.id}`,
        journal: `/dashboard/journal?group=${g.id}`,
        homework: `/dashboard/homework?group=${g.id}`,
      };

  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {g.class_meet_link && (
        <a
          href={g.class_meet_link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${p}-join-class-${g.id}`}
          className={`${BASE} ${g.class_meet_platform === "zoom" ? STYLE.zoom : STYLE.meet}`}
        >
          {g.class_meet_platform === "meet" ? (
            <MeetIcon className="h-4 w-4" />
          ) : g.class_meet_platform === "zoom" ? (
            <ZoomIcon className="h-4 w-4 rounded-sm" />
          ) : (
            <Video className="h-3.5 w-3.5" />
          )}
          {g.class_meet_platform === "zoom"
            ? t("join_class_zoom")
            : g.class_meet_platform === "meet"
            ? t("join_class_meet")
            : t("join_class")}
        </a>
      )}
      <Link to={links.attendance} data-testid={`${p}-attendance-${g.id}`} className={`${BASE} ${STYLE.attendance}`}>
        <CalendarCheck className="h-3.5 w-3.5" /> {t("attendance")}
      </Link>
      <Link to={links.notebook} data-testid={`${p}-notebook-${g.id}`} className={`${BASE} ${STYLE.notebook}`}>
        <NotebookPen className="h-3.5 w-3.5" /> Notebook
      </Link>
      <Link to={links.journal} data-testid={`${p}-journal-${g.id}`} className={`${BASE} ${STYLE.journal}`}>
        <BookMarked className="h-3.5 w-3.5" /> {t("journal")}
      </Link>
      <Link to={links.homework} data-testid={`${p}-homework-${g.id}`} className={`${BASE} ${STYLE.homework}`}>
        <ClipboardList className="h-3.5 w-3.5" /> {t("homework")}
      </Link>
    </div>
  );
};

export default ClassCardActions;
